import {
  FormTab,
  TabbedForm,
  required,
  useNotify,
  usePermissions,
  useRedirect,
  useRefresh,
  useTranslate,
} from "react-admin";
import {
  Group,
  GroupTitle,
  Toolbar,
  TransactionLogsField,
  TransactionNotesInput,
  TransactionNotesIsPrivateInput,
  useSaveMutation,
} from "ra-friendsofbabba";
import { Input, ProjectProposalInput } from "../component/input";
import React, { useMemo } from "react";

import CheckboxItemInput from "./inputs/CheckboxItemInput";
import { DRAWER_WIDTH } from "../config";
import JudgmentItemInput from "./inputs/JudgmentItemInput";
import MeetVerificationTab from "./tabs/MeetVerificationTab";
import PrintToolbarButton from "./buttons/PrintToolbarButton";
import PrintableDocsField from "./fields/PrintableDocsField";
import RepliesTab from "./tabs/RepliesTab";
import TextItemInput from "./inputs/TextItemInput";
import { get } from "lodash";
import { makeStyles } from "@material-ui/core";
import useEvalItems from "./inputs/useEvalItems";
import usePrivileges from "./usePrivileges";
import { useSelector } from "react-redux";

const useStyles = makeStyles((theme) => ({
  root: ({ sidebarOpen, sidebarWidth }) => ({
    "& .MuiTabs-scroller.MuiTabs-fixed": {
      overflowX: "auto !important",
      [theme.breakpoints.up("md")]: {
        maxWidth: sidebarOpen
          ? `calc(100vw - ${sidebarWidth + 64}px)`
          : "calc(100vw - 65px)",
      },
      [theme.breakpoints.down("sm")]: {
        maxWidth: sidebarOpen
          ? `calc(100vw - ${sidebarWidth + 64}px)`
          : "calc(100vw - 65px)",
      },
      [theme.breakpoints.down("xs")]: {
        maxWidth: "calc(100vw - 17px)",
      },
    },
  }),
  help: {
    marginBottom: theme.spacing(2),
  },
}));

const Form = ({ ...props }) => {
  const redirect = useRedirect();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();
  const { loaded, permissions } = usePermissions();
  const isAdmin = useMemo(
    () => loaded && permissions(["admin"]),
    [loaded, permissions]
  );
  const save = useSaveMutation({
    ...props,
    onSuccess: ({ data }, values) => {
      const state = get(values, "state", get(values, "transaction.state"));
      const prevState = get(data, "transaction.state");
      if (
        prevState !== state &&
        (state === "sent" || state === "integrations_requested")
      ) {
        redirect(`${props.basePath}`);
        notify(`resources.technical-report-evals.notify.${state}`);
      } else {
        notify("resources.technical-report-evals.notify.draft_saved");
        refresh();
      }
    },
  });
  const evalItems = useEvalItems();
  const { canView } = usePrivileges({ ...props });
  const sidebarOpen = useSelector((state) => state?.admin?.ui?.sidebarOpen);
  const sidebarWidth = DRAWER_WIDTH;
  const classes = useStyles({ sidebarOpen, sidebarWidth });
  const reportType = props?.record?.technical_report?.technical_report_group_id;
  if (evalItems.length === 0) {
    return null;
  }
  return (
    <TabbedForm
      {...props}
      save={save}
      className={classes.root}
      initialValues={{
        is_private: false,
      }}
      toolbar={
        canView ? (
          <Toolbar useWorkflow maxButtonsToDisplay={1}>
            <PrintToolbarButton reportType={reportType} />
          </Toolbar>
        ) : (
          <Toolbar useWorkflow maxButtonsToDisplay={1} />
        )
      }
    >
      <FormTab label="resources.technical_report_evals.tabs.general">
        <Input
          source="technical_report.executive_project.project_proposal_id"
          project_proposal="technical_report.executive_project.project_proposal"
          validate={required()}
          fullWidth
          component={<ProjectProposalInput />}
        />
        <PrintableDocsField fullWidth reportType={reportType} />
        <Group wrapper>
          <GroupTitle
            title={translate(
              `resources.technical_report_evals.section.${
                reportType <= 2 ? 0 : 1
              }`
            )}
            subTitle={translate(
              `resources.technical_report_evals.section.${
                reportType <= 2 ? 0 : 1
              }.help`
            )}
          />
        </Group>

        <Input
          tab={0}
          bind="progress"
          source="technical_report_eval_items"
          title="resources.technical_report_eval_items.progress.title"
          evalItems={evalItems}
          component={<CheckboxItemInput />}
        />
        <Input
          tab={0}
          bind="progress_overall_rating"
          source="technical_report_eval_items"
          fullWidth
          evalItems={evalItems}
          component={
            <TextItemInput maxLength={2000} final={reportType === 3} />
          }
        />
        <TransactionLogsField admin={isAdmin} source="logs" fullWidth />

        {isAdmin && (
          <TransactionNotesInput
            source="notes"
            helperText="resources.transactions.fields.notes.help"
            fullWidth
          />
        )}
        {isAdmin && (
          <TransactionNotesIsPrivateInput
            admin={isAdmin}
            source="is_private"
            helperText="resources.transactions.fields.is_private.help"
            fullWidth
          />
        )}
      </FormTab>
      <FormTab label="resources.technical_report_evals.tabs.section1">
        <Group wrapper>
          <GroupTitle
            title={translate("resources.technical_report_evals.section1")}
          />
        </Group>
        {props?.record?.technical_report?.technical_report_group_id < 3 && (
          <Input
            bind="activities"
            source="technical_report_eval_items"
            fullWidth
            evalItems={evalItems}
            component={<TextItemInput maxLength={2000} />}
          />
        )}
        {props?.record?.technical_report?.technical_report_group_id === 3 && (
          <Input
            bind="final_activities"
            source="technical_report_eval_items"
            fullWidth
            evalItems={evalItems}
            component={<TextItemInput maxLength={2000} />}
          />
        )}
        <Input
          bind="deliverables"
          source="technical_report_eval_items"
          fullWidth
          evalItems={evalItems}
          component={<TextItemInput maxLength={2000} />}
        />
      </FormTab>
      <FormTab label="resources.technical_report_evals.tabs.section2">
        <Group wrapper>
          <GroupTitle
            title={translate("resources.technical_report_evals.section2")}
            subTitle={translate(
              props?.record?.technical_report?.technical_report_group_id < 3
                ? "resources.technical_report_evals.section2.help"
                : "resources.technical_report_evals.section2.help2"
            )}
          />
        </Group>
        <Input
          bind="collaborations"
          source="technical_report_eval_items"
          title="resources.technical_report_eval_items.collaborations.title"
          evalItems={evalItems}
          component={<CheckboxItemInput />}
        />
        <Input
          bind="collaborations_overall_rating"
          source="technical_report_eval_items"
          fullWidth
          evalItems={evalItems}
          component={<TextItemInput />}
        />
      </FormTab>
      <FormTab label="resources.technical_report_evals.tabs.section3">
        <Group wrapper>
          <GroupTitle
            title={translate("resources.technical_report_evals.section3")}
            subTitle={translate(
              props?.record?.technical_report?.technical_report_group_id < 3
                ? "resources.technical_report_evals.section3.help"
                : "resources.technical_report_evals.section3.help2"
            )}
          />
        </Group>
        <Input
          bind="actions"
          source="technical_report_eval_items"
          title="resources.technical_report_eval_items.actions.title"
          evalItems={evalItems}
          component={<JudgmentItemInput group="2" useNotes />}
        />
        {props?.record?.technical_report?.technical_report_group_id < 3 && (
          <Input
            bind="actions_overall_rating"
            source="technical_report_eval_items"
            fullWidth
            evalItems={evalItems}
            component={<TextItemInput />}
          />
        )}
        {props?.record?.technical_report?.technical_report_group_id === 3 && (
          <Input
            bind="final_actions_overall_rating"
            source="technical_report_eval_items"
            fullWidth
            evalItems={evalItems}
            component={<TextItemInput />}
          />
        )}
      </FormTab>
      <FormTab label="resources.technical_report_evals.tabs.section4">
        <Group wrapper>
          <GroupTitle
            title={translate("resources.technical_report_evals.section4")}
            subTitle={
              props?.record?.technical_report?.technical_report_group_id === 3
                ? translate("resources.technical_report_evals.section4.help")
                : ""
            }
          />
        </Group>
        <Input
          bind="judgments"
          source="technical_report_eval_items"
          evalItems={evalItems}
          component={<JudgmentItemInput group="1" />}
        />
        {props?.record?.technical_report?.technical_report_group_id === 3 && (
          <Group wrapper>
            <Input
              bind="consistency"
              source="technical_report_eval_items"
              fullWidth
              evalItems={evalItems}
              component={<TextItemInput />}
            />
            <GroupTitle
              title={translate(
                "resources.technical_report_evals.strength_points.title"
              )}
              subTitle={translate(
                "resources.technical_report_evals.strength_points.help"
              )}
            />
            {[1, 2, 3].map((i) => (
              <Input
                key={i}
                bind={`strength_point${i}`}
                source="technical_report_eval_items"
                fullWidth
                evalItems={evalItems}
                component={<TextItemInput />}
              />
            ))}
            <GroupTitle
              title={translate(
                "resources.technical_report_evals.weakness_points.title"
              )}
              subTitle={translate(
                "resources.technical_report_evals.weakness_points.help"
              )}
            />
            {[1, 2, 3].map((i) => (
              <Input
                key={i}
                bind={`weakness_point${i}`}
                source="technical_report_eval_items"
                fullWidth
                evalItems={evalItems}
                component={<TextItemInput />}
              />
            ))}
          </Group>
        )}
      </FormTab>
      {reportType === 2 && <MeetVerificationTab />}
      {reportType > 1 && <RepliesTab />}
    </TabbedForm>
  );
};
export default Form;
